/**
 * Which shell a session gets (HS-1630, HS-980 U4): the Installer workspace (`/installer/*`, its own
 * sidebar) or the admin panel.
 *
 * A caller gets the Installer workspace when their roles include `Installer` and **no other role
 * that may sign into the admin panel** — every role whose grant set includes `Auth.Login.Admin`
 * (or is the `FullAccess` wildcard) in the `@smartsash/permissions` matrix. Portal-only roles such
 * as DeviceUser do not count. Rank does not decide it: the backend ranks Installer above
 * InstitutionAdmin, yet an Installer who is also an InstitutionAdmin needs the admin panel.
 *
 * Roles **never authorize**: this only chooses the shell. Every screen is still gated on
 * permissions (`requireRoute`, `usePermission`), and the server decides what a request may read.
 *
 * Until HS-1617 puts `roles` on `/Auth/me`, the dev build honours `?workspace=installer|admin`
 * (kept in sessionStorage; `?workspace=clear` drops it). Production builds read the roles only —
 * sweep 2026-10-12.
 */
import { useAuth, type AuthState, type Session } from '@smartsash/auth';
import { ROLES, type RoleName } from '@smartsash/permissions';

export type Workspace = 'installer' | 'admin';

const INSTALLER_ROLE: RoleName = 'Installer';

/** Every admin-panel role other than Installer, derived from the matrix rather than listed. */
const OTHER_ADMIN_PANEL_ROLES: ReadonlySet<string> = new Set(
  (Object.keys(ROLES) as RoleName[]).filter((name) => {
    if (name === INSTALLER_ROLE) return false;
    const { permissions } = ROLES[name];
    return permissions === 'all' || permissions.includes('Auth.Login.Admin');
  }),
);

/** The shell these role names select. Pure; the rule in the header comment. */
export function workspaceOf(roles: readonly string[]): Workspace {
  if (!roles.includes(INSTALLER_ROLE)) return 'admin';
  return roles.some((role) => OTHER_ADMIN_PANEL_ROLES.has(role)) ? 'admin' : 'installer';
}

// ── Dev override (until HS-1617) ─────────────────────────────────────────────────────────────

const OVERRIDE_PARAM = 'workspace';
const OVERRIDE_STORAGE_KEY = 'admin-workspace';
const OVERRIDE_CLEAR = 'clear';

function isWorkspace(value: unknown): value is Workspace {
  return value === 'installer' || value === 'admin';
}

/** The dev build's forced shell, or `null` — always `null` in a production build. */
export function workspaceOverride(): Workspace | null {
  if (!import.meta.env.DEV) return null;
  try {
    const stored = sessionStorage.getItem(OVERRIDE_STORAGE_KEY);
    return isWorkspace(stored) ? stored : null;
  } catch {
    return null;
  }
}

/**
 * Keeps `?workspace=` from this URL for the rest of the tab's session (dev build only), so the
 * forced shell survives navigation that drops the parameter. `clear` forgets it.
 */
export function rememberWorkspaceOverride(href: string): void {
  if (!import.meta.env.DEV) return;
  const value = new URL(href, 'http://localhost').searchParams.get(OVERRIDE_PARAM);
  if (value === null) return;
  try {
    if (isWorkspace(value)) sessionStorage.setItem(OVERRIDE_STORAGE_KEY, value);
    else if (value === OVERRIDE_CLEAR) sessionStorage.removeItem(OVERRIDE_STORAGE_KEY);
  } catch {
    // Storage blocked: the override simply does not stick.
  }
}

// ── Reading it ───────────────────────────────────────────────────────────────────────────────

/** The shell of a session; a signed-out state reads as admin (the root guard has redirected it). */
export function sessionWorkspace(session: Session | null): Workspace {
  if (session === null) return 'admin';
  return workspaceOverride() ?? workspaceOf(session.roles ?? []);
}

export function stateWorkspace(state: AuthState): Workspace {
  return sessionWorkspace(state.status === 'authenticated' ? state.session : null);
}

/** The signed-in session's shell, for components (the sidebar, the account card). */
export function useWorkspace(): Workspace {
  return stateWorkspace(useAuth().state);
}
